import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { saveGalleryItem } from '../utils/firebaseFunctions';
import Loader from './Loader';
import Sidebar from './Sidebar';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { firestore } from '../firebase.config';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Trash2, Edit2 } from 'lucide-react';

const Gallery = () => {
    const [Heading, setHeading] = useState('');
    const [Description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [field, setField] = useState(false);
    const [alertStatus, setAlertStatus] = useState('danger');
    const [msg, setMsg] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [galleryList, setGalleryList] = useState([]);
    const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [editId, setEditId] = useState(null); // 🆕 ID of gallery item being edited
const formRef = useRef(null);
    const cloudName = "dabhatz7m";
    const uploadPreset = "ImageUpload";

    const toggleSidebar = (state) => {
        setIsSidebarOpen(state);
    };

    const handleImageUpload = (e) => {
        e.preventDefault();
        const file = document.getElementById("imageInput").files[0];
        if (!file) return alert("Please select an image!");

        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);
        formData.append('folder', 'GalleryImages');

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, true);

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                setUploadProgress((event.loaded / event.total) * 100);
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                const data = JSON.parse(xhr.responseText);
                if (data.secure_url) {
                    setUploadedImageUrls([...uploadedImageUrls, data.secure_url]);
                    setUploadProgress(0);
                }
            } else {
                console.error("Failed to upload image to Cloudinary");
                setUploadProgress(0);
            }
        };

        xhr.onerror = () => {
            console.error("Error uploading image:", xhr.statusText);
            setUploadProgress(0);
        };

        xhr.send(formData);
    };

    const handleDeleteImage = (index) => {
        const updatedImages = [...uploadedImageUrls];
        updatedImages.splice(index, 1);
        setUploadedImageUrls(updatedImages);
    };

    const saveDetails = async () => {
        setIsLoading(true);

        if (!Heading || !Description) {
            setField(true);
            setMsg("Required fields can't be empty");
            setAlertStatus("danger");
            toast.error("Required fields can't be empty");
            setTimeout(() => setField(false), 4000);
            setIsLoading(false);
            return;
        }

        if (uploadedImageUrls.length === 0) {
            setField(true);
            setMsg("Please upload at least one image.");
            setAlertStatus("danger");
            toast.error("Please upload at least one image.");
            setTimeout(() => setField(false), 4000);
            setIsLoading(false);
            return;
        }

        try {
            if (editId) {
                // 🆕 Update existing gallery item
                await updateDoc(doc(firestore, 'Gallery', editId), {
                    Heading,
                    Description,
                    imageUrls: [...uploadedImageUrls],
                });
                toast.success("Gallery updated successfully");
                setEditId(null);
            } else {
                // Save new gallery item
                const id = `${Date.now()}`;
                await saveGalleryItem({
                    id,
                    Heading,
                    Description,
                    imageUrls: [...uploadedImageUrls],
                });
                toast.success("Gallery saved successfully");
            }
            clearData();
            fetchGallery();
            setIsLoading(false);
        } catch (error) {
            console.error("Error saving details:", error);
            toast.error("Failed to save details");
            setIsLoading(false);
        }
    };

    const clearData = () => {
        setHeading('');
        setDescription('');
        setUploadedImageUrls([]);
        setEditId(null);
    };

    const fetchGallery = async () => {
        try {
            const querySnapshot = await getDocs(collection(firestore, 'Gallery'));
            const gallery = [];
            querySnapshot.forEach((doc) => {
                gallery.push({ ...doc.data(), docId: doc.id });
            });
            setGalleryList(gallery);
        } catch (error) {
            console.error("Error fetching gallery:", error);
        }
    };

    const handleDeleteGallery = async (docId) => {
        if (!window.confirm("Are you sure you want to delete this item?")) return;
        try {
            await deleteDoc(doc(firestore, 'Gallery', docId));
            toast.success("Deleted successfully");
            fetchGallery();
        } catch (error) {
            console.error("Error deleting gallery:", error);
            toast.error("Failed to delete");
        }
    };

    const handleEditGallery = (item) => {
        setHeading(item.Heading);
        setDescription(item.Description);
        setUploadedImageUrls([...item.imageUrls]);
        setEditId(item.docId); // 🆕 Set the item being edited
         if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    };

    useEffect(() => {
        fetchGallery();
    }, []);

    return (
        <div className='lg:flex h-screen bg-white'>
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={`flex-1 p-1 lg:p-12 overflow-y-auto transition-all duration-300 lg:mt-2 mt-16 ${isSidebarOpen ? 'ml-56 p-0' : 'lg:ml-20 sm:ml-2 '}`}>
                <div ref={formRef}  className={`w-full max-w-4xl mx-auto bg-white shadow-xl rounded-2xl lg:p-8 ${isSidebarOpen ? 'p-1' : 'lg:p-8 p-8'}`}>
                    <h1 className={`lg:text-2xl font-bold text-[#fcaf17] mb-6 ${isSidebarOpen ? 'text-[13px]' : ''}`}>
                        {editId ? "Edit Gallery" : "Add Gallery"}
                    </h1>

                    {field && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`p-3 rounded-lg text-center text-lg font-medium ${alertStatus === "danger" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                        >
                            {msg}
                        </motion.p>
                    )}

                    <div className='lg:space-y-3 space-y-1'>
                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='Heading'>Heading</label>
                            <input
                                id='Heading'
                                type='text'
                                value={Heading}
                                autoComplete='off'
                                placeholder='Enter Heading'
                                onChange={(e) => setHeading(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>

                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor="Description">Description</label>
                            <textarea
                                id="Description"
                                value={Description}
                                autoComplete="off"
                                placeholder="Enter Description"
                                onChange={(e) => setDescription(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>

                        <div className="mb-4">
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor="imageInput">Image Upload</label>
                            <label
                                htmlFor="imageInput"
                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                            >
                                <span>Upload Images</span>
                            </label>
                            <input
                                type="file"
                                id="imageInput"
                                accept="image/*"
                                onChange={handleImageUpload}
                                multiple
                                className="hidden"
                            />
                        </div>

                        {uploadProgress > 0 && (
                            <div className="mt-2">
                                <div className="bg-gray-200 rounded-full h-2.5 mb-4">
                                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                                <p className="text-sm text-gray-600 text-center">{Math.round(uploadProgress)}%</p>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-4 mt-2">
                            {uploadedImageUrls.map((url, index) => (
                                <div key={index} className="relative w-[200px] h-[130px] border rounded-md overflow-hidden">
                                    <img src={url} alt={`Uploaded ${index}`} className="w-full h-full object-cover" />
                                    <button onClick={() => handleDeleteImage(index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                       <div className="flex justify-start mt-4 gap-3">
    <button
        className="bg-blue-600 text-white lg:text-sm text-xs py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-300"
        onClick={saveDetails}
    >
        {isLoading ? <Loader /> : editId ? "Update" : "Save"}
    </button>
    {editId && (
        <button
            className="bg-gray-200 text-gray-700 lg:text-sm text-xs py-2 px-4 rounded-lg hover:bg-gray-300 transition duration-300"
            onClick={clearData}
        >
            Cancel
        </button>
    )}
</div>
                    </div>
                </div>

                {/* --- Gallery Table --- */}
                {galleryList.length > 0 ? (
                    <div className="mt-12 p-8">
                        <h2 className="text-3xl font-extrabold mb-8 text-[#fcaf17] flex items-center gap-3">
                            <span>✨</span> Gallery Collection
                        </h2>
                        <div className="overflow-hidden border border-yellow-200">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="bg-gradient-to-r from-yellow-100 to-yellow-200/70 border-b border-yellow-300 text-[14px] uppercase font-bold tracking-wider text-orange-800">
                                    <tr>
                                        <th className="px-6 py-4">Sl. No.</th>
                                        <th className="px-6 py-4">Heading</th>
                                        <th className="px-6 py-4">Description</th>
                                        <th className="px-6 py-4">Image</th>
                                        <th className="px-6 py-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {galleryList.map((item, index) => (
                                        <tr key={item.docId} className="group bg-white/70 transition-all duration-300 hover:bg-orange-50/70 border-b border-orange-100 last:border-b-0">
                                            <td className="px-6 py-4 font-semibold">{index + 1}</td>
                                            <td className="px-6 py-4">{item.Heading}</td>
                                            <td className="px-6 py-4 text-sm">{item.Description}</td>
                                            <td className="px-6 py-4">
                                                <img src={item.imageUrls[0]} alt={`Slider ${index + 1}`} className="w-28 h-20 object-cover rounded-xl" />
                                            </td>
                                            <td className="px-6 py-4 text-center flex justify-center gap-2">
                                                <button onClick={() => handleEditGallery(item)} className="inline-flex items-center gap-1 text-blue-600 hover:text-white bg-blue-100 hover:bg-blue-500 px-3 py-1 rounded-full text-sm font-semibold">
                                                    <Edit2 className="w-4 h-4" /> Edit
                                                </button>
                                                <button onClick={() => handleDeleteGallery(item.docId)} className="inline-flex items-center gap-1 text-red-600 hover:text-white bg-red-100 hover:bg-red-500 px-3 py-1 rounded-full text-sm font-semibold">
                                                    <Trash2 className="w-4 h-4" /> Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="mt-12 p-8 text-center text-gray-500 text-lg italic bg-white border border-orange-100 rounded-xl shadow-sm">
                        No gallery items yet. Add some!
                    </div>
                )}
            </div>
        </div>
    );
};

export default Gallery;